import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Upload as UploadIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { leadsService, Lead } from '../services/leads';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface ICP {
  id: string;
  name: string;
}

export default function Leads() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openImport, setOpenImport] = useState(false);
  const [openDiscover, setOpenDiscover] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [icps, setIcps] = useState<ICP[]>([]);
  const [discoverForm, setDiscoverForm] = useState({
    icp_id: '',
    keywords: '',
    location: '',
    max_results: 50,
  });

  const workspaceId = 'default-workspace';

  useEffect(() => {
    loadLeads();
    loadICPs();
  }, []);

  const loadLeads = async () => {
    try {
      setLoading(true);
      const data = await leadsService.getLeads();
      setLeads(data || []);
    } catch (err: any) {
      console.error('Failed to load leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadICPs = async () => {
    try {
      const response = await axios.get(`${API_URL}/icps`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setIcps(response.data || []);
    } catch (err: any) {
      console.error('Failed to load ICPs:', err);
    }
  };

  const handleDiscoverLeads = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate ICP selection (required for effective lead discovery)
      if (!discoverForm.icp_id || !discoverForm.icp_id.trim()) {
        setError('Please select an Ideal Customer Profile (ICP) to guide lead discovery');
        setLoading(false);
        return;
      }
      
      // Validate max_results range (must match backend: 10-200)
      const maxResults = parseInt(String(discoverForm.max_results));
      if (isNaN(maxResults) || maxResults < 10 || maxResults > 200) {
        setError('Max results must be between 10 and 200');
        setLoading(false);
        return;
      }
      
      // Transform keywords string to array for backend
      const keywords = discoverForm.keywords
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0);
      
      // Build payload with required and optional fields
      const payload: any = {
        icp_id: discoverForm.icp_id,  // Required: guides agent on what leads to find
        keywords: keywords,            // Optional: refines search criteria
        max_results: maxResults,       // Validated: 10-200
      };
      
      // Add location if provided
      if (discoverForm.location && discoverForm.location.trim()) {
        payload.location = discoverForm.location;
      }
      
      console.log('Lead discovery payload:', payload);
      
      // Step 1: Create workflow via orchestrator
      const response = await axios.post(
        `${API_URL}/leads/discover`,
        payload,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        }
      );
      
      const workflowId = response.data.workflow_id;
      setSuccess(`Lead discovery workflow created! Workflow ID: ${workflowId}. Executing multi-agent workflow...`);
      
      // Step 2: Automatically execute the workflow (orchestrator delegates to agents)
      console.log(`Attempting to execute workflow: ${workflowId}`);
      try {
        const executeResponse = await axios.post(
          `${API_URL}/workflows/${workflowId}/execute`,
          {},
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
          }
        );
        console.log('Workflow execution response:', executeResponse.data);
        
        setOpenDiscover(false);
        setSuccess(`Lead discovery workflow executing! Our AI agents (Lead Sourcing → Lead Enrichment → Lead Scoring) are working. View progress in Workflows page.`);
        setDiscoverForm({ icp_id: '', keywords: '', location: '', max_results: 50 });
        
        // Reload leads after workflow has time to complete
        setTimeout(() => loadLeads(), 5000);
      } catch (executeErr: any) {
        console.error('Workflow execution error:', executeErr);
        console.error('Execute error details:', {
          status: executeErr.response?.status,
          data: executeErr.response?.data,
          message: executeErr.message
        });
        // Still show success for workflow creation, but warn about execution
        setOpenDiscover(false);
        setSuccess(`Workflow created (ID: ${workflowId}) but execution failed. Check logs or manually execute from Workflows page.`);
      }
    } catch (err: any) {
      console.error('Lead discovery error:', err);
      setError(err.response?.data?.detail || 'Failed to discover leads');
    } finally {
      setLoading(false);
    }
  };

  const handleScoreLeads = async () => {
    try {
      await leadsService.scoreLeads();
      setError(null);
      setSuccess('Lead scoring started successfully!');
      loadLeads();
    } catch (err: any) {
      setError('Failed to score leads');
    }
  };

  const handleImportLeads = async () => {
    if (!csvFile) return;
    try {
      const content = await csvFile.text();
      await leadsService.importLeads(content);
      setOpenImport(false);
      setCsvFile(null);
      setSuccess('Leads imported successfully!');
      loadLeads();
    } catch (err: any) {
      setError('Failed to import leads');
    }
  };

  const getSegmentColor = (segment: string): any => {
    const colors: any = {
      hot: 'error',
      warm: 'warning',
      nurture: 'info',
      cold: 'default',
    };
    return colors[segment] || 'default';
  };

  const columns: GridColDef[] = [
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => params.row.contact?.email || 'N/A',
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) =>
        `${params.row.contact?.first_name || ''} ${params.row.contact?.last_name || ''}`.trim() ||
        'N/A',
    },
    {
      field: 'company',
      headerName: 'Company',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row.account?.name || 'N/A',
    },
    {
      field: 'score',
      headerName: 'Score',
      width: 80,
      valueGetter: (params) => params.row.score || 0,
    },
    {
      field: 'segment',
      headerName: 'Segment',
      width: 100,
      renderCell: (params) =>
        params.row.segment ? (
          <Chip label={params.row.segment} color={getSegmentColor(params.row.segment)} size="small" />
        ) : null,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => <Chip label={params.row.status} size="small" variant="outlined" />,
    },
  ];

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 0 } }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: { xs: 2, sm: 3 },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 },
        width: '100%'
      }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>Leads</Typography>
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, auto)' },
          gap: { xs: 1, sm: 1 },
          width: { xs: '100%', sm: 'auto' }
        }}>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />} 
            onClick={loadLeads}
            size="small"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Refresh
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<SearchIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />} 
            onClick={() => setOpenDiscover(true)}
            size="small"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Discover
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<AssessmentIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />} 
            onClick={handleScoreLeads}
            size="small"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Score
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<UploadIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />} 
            onClick={() => setOpenImport(true)}
            size="small"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Import
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ 
        height: { xs: 400, sm: 500, md: 600 }, 
        width: '100%',
        '& .MuiDataGrid-root': {
          border: 'none',
        },
        '& .MuiDataGrid-cell': {
          fontSize: { xs: '0.75rem', sm: '0.875rem' },
        },
        '& .MuiDataGrid-columnHeaders': {
          fontSize: { xs: '0.75rem', sm: '0.875rem' },
        }
      }}>
        <DataGrid
          rows={leads}
          columns={columns}
          loading={loading}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
        />
      </Paper>

      <Dialog open={openImport} onClose={() => setOpenImport(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Leads</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Upload a CSV file with lead data. Required columns: email
          </Typography>
          <Button variant="outlined" component="label" fullWidth>
            Choose CSV File
            <input
              type="file"
              hidden
              accept=".csv"
              onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
            />
          </Button>
          {csvFile && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Selected: {csvFile.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImport(false)}>Cancel</Button>
          <Button onClick={handleImportLeads} variant="contained" disabled={!csvFile}>
            Import
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discover Leads Dialog */}
      <Dialog open={openDiscover} onClose={() => setOpenDiscover(false)} maxWidth="md" fullWidth>
        <DialogTitle>Discover New Leads</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="textSecondary" mb={3}>
              Use AI to discover qualified leads matching your Ideal Customer Profile
            </Typography>

            {icps.length === 0 ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No ICPs found. Please create an Ideal Customer Profile first to guide lead discovery.
              </Alert>
            ) : (
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Select ICP *</InputLabel>
                <Select
                  value={discoverForm.icp_id}
                  label="Select ICP *"
                  onChange={(e) => setDiscoverForm({ ...discoverForm, icp_id: e.target.value })}
                  required
                >
                  {icps.map((icp) => (
                    <MenuItem key={icp.id} value={icp.id}>
                      {icp.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth
              label="Keywords (Optional)"
              value={discoverForm.keywords}
              onChange={(e) => setDiscoverForm({ ...discoverForm, keywords: e.target.value })}
              margin="normal"
              helperText="Comma-separated keywords to refine search (e.g., SaaS, B2B, Enterprise)"
              disabled={icps.length === 0}
            />

            <TextField
              fullWidth
              label="Location (Optional)"
              value={discoverForm.location}
              onChange={(e) => setDiscoverForm({ ...discoverForm, location: e.target.value })}
              margin="normal"
              helperText="Geographic location (e.g., United States, San Francisco)"
              disabled={icps.length === 0}
            />

            <TextField
              fullWidth
              label="Max Results"
              type="number"
              value={discoverForm.max_results}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                setDiscoverForm({ ...discoverForm, max_results: isNaN(val) ? 50 : Math.min(200, Math.max(10, val)) });
              }}
              margin="normal"
              inputProps={{ min: 10, max: 200, step: 10 }}
              helperText="Number of leads to discover (10-200)"
              disabled={icps.length === 0}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDiscover(false)}>Cancel</Button>
          <Button
            onClick={handleDiscoverLeads}
            variant="contained"
            disabled={!discoverForm.icp_id || loading || icps.length === 0}
          >
            {loading ? <CircularProgress size={24} /> : 'Discover Leads'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
